import { useState } from "react";
import { Drawer, IconButton } from "@mui/material";
import { TuneOutlined } from "@mui/icons-material";
import { MdAutoAwesome, MdFileUpload } from "react-icons/md";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { namespace } from "@common-config/constants";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { automation } from "@common-reducers/AutomationReducer";
import { MODAL, modalActions } from "@common-reducers/ModalReducer";

import PersonFilterShadcn from "../person-button-filter/person-filter-shadcn";
import OpenFilterPanelButtonShadcn from "../filter-button/open-filters-panel-button-shadcn";
import ColumnsDropdownShadcn from "./columns-dropdown-shadcn";
import TaskPanelHeaderImportButton from "../filter-button/import-button";
import ToolbarButton from "@common-components/toolbar-button";

import classes from "./mobile-toolbar-menu.module.scss";

type Props = {
  isDisplayPersonButton?: boolean;
  isDisplayColumnsButton?: boolean;
  isDisplayImportButton?: boolean;
  isDisplayExportButton?: boolean;
  isFromCampaign?: boolean;
  board?: { displayBetaFeatures?: boolean } | undefined;
};

export default function MobileToolbarMenu({
  isDisplayPersonButton = true,
  isDisplayColumnsButton = true,
  isDisplayImportButton = true,
  isDisplayExportButton = true,
  isFromCampaign = false,
  board,
}: Props) {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const direction = useAppSelector((state) => state.UserReducer.direction);

  const [open, setOpen] = useState(false);

  const closeDrawer = () => setOpen(false);

  function openAutomationsModal() {
    closeDrawer();
    dispatch(automation.setIsShown(true));
  }

  function openExportModal() {
    closeDrawer();
    dispatch(modalActions.setComponentToRender(MODAL.EXPORT_TO_EXCEL));
  }

  return (
    <>
      <IconButton
        aria-label={t("mobileToolbar.options", "Board options")}
        onClick={() => setOpen(true)}
        size="small"
        className={classes.optionsButton}
      >
        <TuneOutlined fontSize="small" />
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={closeDrawer}
        variant="temporary"
        // Radix (shadcn) popovers/menus portal to the shadow-DOM portal container,
        // which lives outside this MUI modal; without disabling focus enforcement
        // MUI would steal focus back and break typing inside those popovers.
        disableEnforceFocus
        PaperProps={{
          className: classes.drawerPaper,
          // dir must live on the scrolling paper so RTL applies to the whole sheet
          dir: direction,
        }}
      >
        <div className={classes.sheet}>
          <div className={classes.handle} aria-hidden />

          <div className={classes.header}>
            <span className={classes.title}>
              {t("mobileToolbar.options", "Board options")}
            </span>
            <IconButton
              aria-label={t("mobileToolbar.close", "Close")}
              onClick={closeDrawer}
              size="small"
              className={classes.closeButton}
            >
              <X className="h-4 w-4" />
            </IconButton>
          </div>

          <div className={classes.rows}>
            {isDisplayPersonButton && (
              <div className={classes.row}>
                <span className={classes.rowLabel}>
                  {t("mobileToolbar.person", "Person")}
                </span>
                <div className={classes.control}>
                  <PersonFilterShadcn />
                </div>
              </div>
            )}

            <div className={classes.row}>
              <span className={classes.rowLabel}>
                {t("mobileToolbar.filter", "Filter")}
              </span>
              <div className={classes.control}>
                <OpenFilterPanelButtonShadcn />
              </div>
            </div>

            {isDisplayColumnsButton && (
              <div className={classes.row}>
                <span className={classes.rowLabel}>
                  {t("mobileToolbar.columns", "Columns")}
                </span>
                <div className={classes.control}>
                  <ColumnsDropdownShadcn />
                </div>
              </div>
            )}

            {isDisplayImportButton && (
              <div className={classes.row}>
                <span className={classes.rowLabel}>
                  {t("mobileToolbar.import", "Import")}
                </span>
                {/* Import button dispatches internally; close the sheet on the way. */}
                <div className={classes.control} onClickCapture={closeDrawer}>
                  <TaskPanelHeaderImportButton isFromCampaign={isFromCampaign} />
                </div>
              </div>
            )}

            {isDisplayExportButton && (
              <div className={classes.row}>
                <span className={classes.rowLabel}>
                  {t("mobileToolbar.export", "Export")}
                </span>
                <div className={classes.control}>
                  <ToolbarButton
                    startIconSVG={MdFileUpload}
                    onClickAction={openExportModal}
                    buttonText={t("tasksPanelHeader.export")}
                    toolTipTitle={t("tasksPanelHeader.exportDataTooltip")}
                  />
                </div>
              </div>
            )}

            {board?.displayBetaFeatures && (
              <div className={classes.row}>
                <span className={classes.rowLabel}>
                  {t("mobileToolbar.automations", "Automations")}
                </span>
                <div className={classes.control}>
                  <ToolbarButton
                    onClickAction={openAutomationsModal}
                    startIcon={<MdAutoAwesome style={{ width: 16, height: 16 }} />}
                    buttonText="Automations"
                    toolTipTitle="Create automations"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
}
